import { useMyOnboardingStatus, useUsers } from '@/api/hooks/use-users';
import { useStrategies } from '@/api/hooks/use-strategies';
import { useBacktestRuns } from '@/api/hooks/use-backtests';
import { useAuthStore } from '@/features/auth/store/auth-store';
import { useChecklistStore, type ChecklistStepId } from '../store/checklist-store';

export type StepState = 'pending' | 'active' | 'done' | 'skipped' | 'blocked';

export interface ChecklistStep {
  id: ChecklistStepId;
  state: StepState;
  /** Route the CTA navigates to. */
  cta: string;
  /** When `true`, this step has no Skip button (Identity rule 3). */
  hardDep?: boolean;
}

/**
 * Composes backend OnboardingStatus + client-side derived signals into a uniform
 * checklist model. Steps 2 (strategy), 3 (backtest), 5 (team) lack backend
 * fields today — derived from list-page hooks per docs/design/onboarding-checklist.md
 * § Decision 3.
 */
export function useChecklistProgress(): {
  steps: ChecklistStep[];
  completedCount: number;
  total: number;
  allComplete: boolean;
  isLoading: boolean;
} {
  const role = useAuthStore((s) => s.user?.role);
  const skipped = useChecklistStore((s) => s.skipped);

  const { data: status, isLoading: loadingStatus } = useMyOnboardingStatus();
  const { data: strategies, isLoading: loadingStrategies } = useStrategies();
  const { data: backtests, isLoading: loadingBacktests } = useBacktestRuns({ limit: 1 });
  const { data: users, isLoading: loadingUsers } = useUsers({ limit: 2 });

  const isLoading =
    loadingStatus || loadingStrategies || loadingBacktests || (role === 'ADMIN' && loadingUsers);

  const hasVenue = status?.onboarding?.has_exchange_account ?? false;
  const hasBot = status?.onboarding?.has_started_bot ?? false;
  const hasStrategy = (strategies?.length ?? 0) > 0;
  const hasBacktest = (backtests?.total ?? 0) > 0;
  const hasTeam = (users?.total ?? 0) > 1;

  const showTeam = role === 'ADMIN';
  const total = showTeam ? 5 : 4;

  const baseSteps: Array<Omit<ChecklistStep, 'state'> & { done: boolean; gate?: boolean }> = [
    { id: 'venue', cta: '/venue-connections', done: hasVenue, hardDep: true },
    { id: 'strategy', cta: '/strategies', done: hasStrategy },
    { id: 'backtest', cta: '/backtests', done: hasBacktest, gate: !hasStrategy },
    { id: 'bot', cta: '/bots/create', done: hasBot, gate: !hasVenue },
  ];
  if (showTeam) {
    baseSteps.push({ id: 'team', cta: '/admin', done: hasTeam });
  }

  // Find first non-done, non-skipped step → that's the active step.
  const firstActiveIdx = baseSteps.findIndex((s) => !s.done && !skipped.includes(s.id));

  const steps: ChecklistStep[] = baseSteps.map((s, i) => {
    let state: StepState;
    if (s.done) state = 'done';
    else if (skipped.includes(s.id)) state = 'skipped';
    else if (i === firstActiveIdx) state = 'active';
    else state = 'pending';
    return { id: s.id, state, cta: s.cta, hardDep: s.hardDep };
  });

  const completedCount = steps.filter((s) => s.state === 'done' || s.state === 'skipped').length;
  const allComplete = completedCount === total;

  return { steps, completedCount, total, allComplete, isLoading };
}
